import { useState } from 'react';
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Alert,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import AddressFields from '@/components/ui/AddressFields';
import EurCurrencyField from '@/components/form/EurCurrencyField';
import { CoInvestorsStep, CoInvestorFormRow } from './CoInvestorsStep';
import {
  CreateCoInvestmentRequestBody,
  CoInvestorInput,
  InvestmentType,
} from '@inversor-hub/types';
import { investmentTypeLabels } from '@/features/investments/constants';

const steps = ['Datos del inmueble', 'Coinversores'];

interface CoInvestmentWizardProps {
  onSubmit: (data: CreateCoInvestmentRequestBody, mainImageFile?: File | null) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  error: string | null;
  onErrorDismiss: () => void;
}

interface PropertyFormData {
  address: string;
  city: string;
  province: string;
  postalCode: string;
  type: InvestmentType | '';
  acquisitionPrice: number | null;
  monthlyRent: number | null;
  minContributionAmount: number | null;
  maxContributionAmount: number | null;
  fundraisingEndDate: string;
  propertyRegistryVolume: string;
  propertyRegistryBook: string;
  propertyRegistryFolio: string;
  propertyRegistryEntry: string;
  propertyRegistryPropertyNumber: string;
  propertyCadastralReference: string;
}

const defaultPropertyData: PropertyFormData = {
  address: '',
  city: '',
  province: '',
  postalCode: '',
  type: '',
  acquisitionPrice: null,
  monthlyRent: null,
  minContributionAmount: null,
  maxContributionAmount: null,
  fundraisingEndDate: '',
  propertyRegistryVolume: '',
  propertyRegistryBook: '',
  propertyRegistryFolio: '',
  propertyRegistryEntry: '',
  propertyRegistryPropertyNumber: '',
  propertyCadastralReference: '',
};

const defaultCoInvestors: CoInvestorFormRow[] = [
  {
    email: '',
    contributionAmount: null,
    ownershipPercentage: null,
    isPaid: false,
    feeType: '',
    feeAmount: null,
    feePercentage: null,
  },
  {
    email: '',
    contributionAmount: null,
    ownershipPercentage: null,
    isPaid: false,
    feeType: '',
    feeAmount: null,
    feePercentage: null,
  },
];

export const CoInvestmentWizard = ({
  onSubmit,
  onCancel,
  isLoading,
  error,
  onErrorDismiss,
}: CoInvestmentWizardProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [propertyData, setPropertyData] = useState<PropertyFormData>(defaultPropertyData);
  const [coInvestors, setCoInvestors] = useState<CoInvestorFormRow[]>(defaultCoInvestors);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validatePropertyStep = (): boolean => {
    if (!propertyData.address.trim()) {
      setValidationError('La dirección es obligatoria');
      return false;
    }
    if (!propertyData.city.trim()) {
      setValidationError('La población es obligatoria');
      return false;
    }
    if (!propertyData.province.trim()) {
      setValidationError('La provincia es obligatoria');
      return false;
    }
    if (!propertyData.postalCode.trim()) {
      setValidationError('El código postal es obligatorio');
      return false;
    }
    if (!propertyData.type) {
      setValidationError('El tipo de inversión es obligatorio');
      return false;
    }
    if (propertyData.acquisitionPrice === null || propertyData.acquisitionPrice <= 0) {
      setValidationError('El precio de adquisición es obligatorio');
      return false;
    }
    if (propertyData.minContributionAmount === null || propertyData.minContributionAmount <= 0) {
      setValidationError('La aportación mínima es obligatoria');
      return false;
    }
    if (propertyData.maxContributionAmount === null || propertyData.maxContributionAmount <= 0) {
      setValidationError('La aportación máxima es obligatoria');
      return false;
    }
    if (propertyData.maxContributionAmount < propertyData.minContributionAmount) {
      setValidationError('La aportación máxima debe ser mayor o igual que la mínima');
      return false;
    }
    if (!propertyData.fundraisingEndDate.trim()) {
      setValidationError('La fecha fin de captación es obligatoria');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validatePropertyStep()) return;
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setValidationError(null);
  };

  const validateCoInvestors = (): boolean => {
    const oneAsRate = 1;
    const rateEpsilon = 0.0001;

    for (const ci of coInvestors) {
      if (!ci.email || !ci.email.includes('@')) {
        setValidationError('Todos los coinversores deben tener un email válido');
        return false;
      }
      if (
        ci.ownershipPercentage === null ||
        ci.ownershipPercentage <= 0 ||
        ci.ownershipPercentage > oneAsRate
      ) {
        setValidationError('Todos los coinversores deben tener un porcentaje entre 0% y 100%');
        return false;
      }
    }

    const totalRate = coInvestors.reduce((sum, ci) => sum + (ci.ownershipPercentage ?? 0), 0);
    if (Math.abs(totalRate - oneAsRate) > rateEpsilon) {
      setValidationError(
        `Los porcentajes deben sumar 100%. Actual: ${(totalRate * 100).toFixed(2)}%`
      );
      return false;
    }

    const emails = coInvestors.map((ci) => ci.email.toLowerCase());
    if (new Set(emails).size !== emails.length) {
      setValidationError('No puede haber emails duplicados');
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleFinalSubmit = async () => {
    if (!validateCoInvestors()) return;

    const coInvestorInputs: CoInvestorInput[] = coInvestors.map((ci) => ({
      email: ci.email,
      ownershipPercentage: ci.ownershipPercentage!,
      isPaid: ci.isPaid,
      feeType: undefined,
      feeAmount: undefined,
      feePercentage: undefined,
    }));

    const payload: CreateCoInvestmentRequestBody = {
      address: propertyData.address,
      city: propertyData.city,
      province: propertyData.province,
      postalCode: propertyData.postalCode,
      type: propertyData.type as InvestmentType,
      minContributionAmount: propertyData.minContributionAmount ?? 0,
      maxContributionAmount: propertyData.maxContributionAmount ?? 0,
      fundraisingEndDate: propertyData.fundraisingEndDate,
      propertyRegistryVolume: propertyData.propertyRegistryVolume || undefined,
      propertyRegistryBook: propertyData.propertyRegistryBook || undefined,
      propertyRegistryFolio: propertyData.propertyRegistryFolio || undefined,
      propertyRegistryEntry: propertyData.propertyRegistryEntry || undefined,
      propertyRegistryPropertyNumber: propertyData.propertyRegistryPropertyNumber || undefined,
      propertyCadastralReference: propertyData.propertyCadastralReference || undefined,
      capex: {
        acquisitionPrice: propertyData.acquisitionPrice ?? 0,
      },
      monthlyRent: propertyData.monthlyRent ?? undefined,
      coInvestors: coInvestorInputs,
    };

    await onSubmit(payload);
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {(error || validationError) && (
        <Alert severity="error" onClose={onErrorDismiss} sx={{ mb: 2 }}>
          {error || validationError}
        </Alert>
      )}

      {/* Step 0: Property + basic financials */}
      {activeStep === 0 && (
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Datos del inmueble
          </Typography>

          <Stack spacing={2}>
            <AddressFields
              formData={{
                address: propertyData.address,
                city: propertyData.city,
                postalCode: propertyData.postalCode,
                province: propertyData.province,
              }}
              onChange={({ address, city, postalCode, province }) =>
                setPropertyData((prev) => ({ ...prev, address, city, postalCode, province }))
              }
            />

            <FormControl fullWidth>
              <InputLabel>Tipo de inversión *</InputLabel>
              <Select
                value={propertyData.type}
                onChange={(e) =>
                  setPropertyData((prev) => ({ ...prev, type: e.target.value as InvestmentType }))
                }
                label="Tipo de inversión *"
              >
                {Object.entries(investmentTypeLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <EurCurrencyField
              label="Precio de adquisición *"
              value={propertyData.acquisitionPrice}
              onChange={(val) => setPropertyData((prev) => ({ ...prev, acquisitionPrice: val }))}
            />

            <EurCurrencyField
              label="Alquiler mensual"
              value={propertyData.monthlyRent}
              onChange={(val) => setPropertyData((prev) => ({ ...prev, monthlyRent: val }))}
            />

            <EurCurrencyField
              label="Aportación mínima *"
              value={propertyData.minContributionAmount}
              onChange={(val) =>
                setPropertyData((prev) => ({ ...prev, minContributionAmount: val }))
              }
            />

            <EurCurrencyField
              label="Aportación máxima *"
              value={propertyData.maxContributionAmount}
              onChange={(val) =>
                setPropertyData((prev) => ({ ...prev, maxContributionAmount: val }))
              }
            />

            <TextField
              label="Fin de captación *"
              type="date"
              value={propertyData.fundraisingEndDate}
              onChange={(e) =>
                setPropertyData((prev) => ({ ...prev, fundraisingEndDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Tomo registro"
              value={propertyData.propertyRegistryVolume}
              onChange={(e) =>
                setPropertyData((prev) => ({
                  ...prev,
                  propertyRegistryVolume: e.target.value,
                }))
              }
              fullWidth
            />

            <TextField
              label="Libro registro"
              value={propertyData.propertyRegistryBook}
              onChange={(e) =>
                setPropertyData((prev) => ({
                  ...prev,
                  propertyRegistryBook: e.target.value,
                }))
              }
              fullWidth
            />

            <TextField
              label="Folio registro"
              value={propertyData.propertyRegistryFolio}
              onChange={(e) =>
                setPropertyData((prev) => ({
                  ...prev,
                  propertyRegistryFolio: e.target.value,
                }))
              }
              fullWidth
            />

            <TextField
              label="Inscripción registro"
              value={propertyData.propertyRegistryEntry}
              onChange={(e) =>
                setPropertyData((prev) => ({
                  ...prev,
                  propertyRegistryEntry: e.target.value,
                }))
              }
              fullWidth
            />

            <TextField
              label="Finca registro"
              value={propertyData.propertyRegistryPropertyNumber}
              onChange={(e) =>
                setPropertyData((prev) => ({
                  ...prev,
                  propertyRegistryPropertyNumber: e.target.value,
                }))
              }
              fullWidth
            />

            <TextField
              label="Referencia catastral"
              value={propertyData.propertyCadastralReference}
              onChange={(e) =>
                setPropertyData((prev) => ({
                  ...prev,
                  propertyCadastralReference: e.target.value,
                }))
              }
              fullWidth
            />
          </Stack>

          <Box display="flex" justifyContent="space-between" sx={{ mt: 3 }}>
            <Button onClick={onCancel}>Cancelar</Button>
            <Button variant="contained" onClick={handleNext}>
              Siguiente: Coinversores
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 1: Co-investors */}
      {activeStep === 1 && (
        <Box>
          <CoInvestorsStep
            coInvestors={coInvestors}
            onChange={setCoInvestors}
            acquisitionPrice={propertyData.acquisitionPrice ?? 0}
          />
          <Box display="flex" justifyContent="space-between" sx={{ mt: 3 }}>
            <Button onClick={handleBack} disabled={isLoading}>
              Anterior
            </Button>
            <Button
              variant="contained"
              onClick={handleFinalSubmit}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
            >
              {isLoading ? 'Creando co-inversión...' : 'Crear co-inversión'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
